import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography,
  Box,
  Button, 
  Divider,
  CircularProgress,
  Tooltip,
  Stack,
  alpha,
  useMediaQuery,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { formatDistanceToNow } from 'date-fns';
import useNotifications from '../../../hooks/useNotifications';
import useAuth from '../../../hooks/useAuth';

export default function NotificationPopover() {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const hasMarkedOnOpenRef = useRef(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  // Phone only. Every sx below that reads this leaves the desktop branch on
  // the values it already had.
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  // Members have no standalone notifications page to send them to; the feed
  // they can see is the one in this popover.
  const hasFullPage = user?.role === 'org';

  const {
    notifications,
    unreadCount,
    isConnected,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
  } = useNotifications();

  const handleOpen = () => {
    setOpen(true);
    hasMarkedOnOpenRef.current = false;
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  // Auto-mark visible unread notifications as read after 2 seconds
  useEffect(() => {
    if (open && !hasMarkedOnOpenRef.current && unreadCount > 0) {
      const timer = setTimeout(() => {
        // Mark top 5 unread notifications as read
        const unreadNotifications = notifications
          .filter((n) => !n.is_read)
          .slice(0, 5);

        unreadNotifications.forEach((notif) => {
          markAsRead(notif.id);
        });

        hasMarkedOnOpenRef.current = true;
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [open, notifications, unreadCount, markAsRead]);

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          padding: 0,
          width: 44,
          height: 44,
          ...(open && {
            bgcolor: (theme) =>
              alpha(theme.palette.primary.main, theme.palette.action.focusOpacity),
          }),
        }}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <Icon icon="eva:bell-fill" width={24} height={24} />
        </Badge>

        {/* Connection status indicator */}
        {!isConnected && (
          <Tooltip title="Reconnecting..." placement="bottom">
            <Box
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: 'warning.main',
                border: (theme) => `2px solid ${theme.palette.background.paper}`,
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': {
                    opacity: 1,
                  },
                  '50%': {
                    opacity: 0.5,
                  },
                },
              }}
            />
          </Tooltip>
        )}
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        marginThreshold={isMobile ? 0 : 16}
        PaperProps={{
          sx: {
            mt: 1.5,
            ml: 0.75,
            width: 380,
            maxHeight: 650,
            overflow: 'hidden',
            boxShadow: (theme) => theme.customShadows?.z20,
            ...(isMobile && {
              // A 380px card anchored to the right edge of a 390px phone
              // leaves the message text about four words wide. Full bleed
              // instead, with the tray hanging off the header.
              mt: 1,
              ml: 0,
              left: '8px !important',
              right: 8,
              width: 'calc(100vw - 16px)',
              maxWidth: 'calc(100vw - 16px)',
              maxHeight: 'calc(100vh - 96px)',
              borderRadius: 2,
            }),
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
            px: { xs: 2, sm: 2.5 },
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            ...(isMobile && { position: 'sticky', top: 0, zIndex: 1 }),
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {unreadCount === 0
                ? 'No unread messages'
                : `You have ${unreadCount} unread ${unreadCount === 1 ? 'message' : 'messages'}`}
            </Typography>
          </Box>

          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <Button
                size="small"
                color="primary"
                onClick={handleMarkAllAsRead}
                startIcon={<Icon icon="eva:checkmark-circle-2-fill" />}
              >
                Clear all
              </Button>
            </Tooltip>
          )}
        </Box>

        <Divider />

        {/* Error State */}
        {error && (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
            }}
          >
            <Icon icon="eva:alert-circle-fill" width={48} height={48} color="#FF4842" />
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
            <Button size="small" sx={{ mt: 1 }} onClick={() => window.location.reload()}>
              Retry
            </Button>
          </Box>
        )}

        {/* Loading State */}
        {isLoading && notifications.length === 0 && (
          <Box
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              Loading notifications...
            </Typography>
          </Box>
        )}

        {/* Notifications List */}
        <List
          disablePadding
          sx={{
            maxHeight: { xs: 'calc(100vh - 220px)', sm: 500 },
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': {
              width: 8,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: (theme) => alpha(theme.palette.grey[600], 0.48),
              borderRadius: 4,
            },
          }}
        >
          {!isLoading && notifications.length === 0 && (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
              }}
            >
              <Icon icon="eva:bell-fill" width={64} height={64} color="#C4CDD5" />
              <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
                No notifications yet
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: 'text.disabled' }}>
                We'll notify you when something arrives
              </Typography>
            </Box>
          )}

          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkAsRead={markAsRead}
              onClose={handleClose}
              navigate={navigate}
            />
          ))}
        </List>

        {/* Footer */}
        {notifications.length > 0 && hasFullPage && (
          <>
            <Divider />
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Button
                fullWidth
                size="small"
                onClick={() => {
                  navigate('/admin/notifications');
                  handleClose();
                }}
              >
                View all notifications
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
}

// ----------------------------------------------------------------------

function NotificationItem({ notification, onMarkAsRead, onClose, navigate }) {
  const handleClick = () => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }

    // If there's an action URL, navigate to it
    if (notification.action_url) {
      navigate(notification.action_url);
      onClose();
    }
  };

  const handleActionClick = (e) => {
    e.stopPropagation();
    onMarkAsRead(notification.id);

    if (notification.action_url) {
      navigate(notification.action_url);
      onClose();
    }
  };

  // Get icon and color based on notification type or severity
  const getNotificationStyle = () => {
    // Priority 1: Check severity
    switch (notification.severity) {
      case 'urgent':
        return {
          icon: 'eva:alert-circle-fill',
          color: '#FF4842',
          bgcolor: alpha('#FF4842', 0.16),
        };
      case 'warning':
        return {
          icon: 'eva:alert-circle-fill',
          color: '#FFC107',
          bgcolor: alpha('#FFC107', 0.16),
        };
      default:
        break;
    }

    // Priority 2: Check notification type
    switch (notification.notification_type) {
      case 'payment_reminder':
      case 'urgent_payment':
        return {
          icon: 'eva:alert-circle-fill',
          color: '#FF4842',
          bgcolor: alpha('#FF4842', 0.16),
        };
      case 'subscription_expiry':
      case 'access_removal':
        return {
          icon: 'eva:alert-circle-fill',
          color: '#FFC107',
          bgcolor: alpha('#FFC107', 0.16),
        };
      case 'visitor_checkin':
        return {
          icon: 'eva:checkmark-circle-2-fill',
          color: '#54D62C',
          bgcolor: alpha('#54D62C', 0.16),
        };
      case 'marketing':
        return { 
          icon: 'eva:info-fill',
          color: '#00B8D9',
          bgcolor: alpha('#00B8D9', 0.16),
        };
      default:
        return {
          icon: 'eva:info-fill',
          color: '#1890FF',
          bgcolor: alpha('#1890FF', 0.16),
        };
    }
  };

  const style = getNotificationStyle();

  return (
    <ListItem
      button
      onClick={handleClick}
      sx={{
        py: 2,
        px: { xs: 2, sm: 2.5 },
        alignItems: { xs: 'flex-start', sm: 'center' },
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        cursor: 'pointer',
        transition: 'all 0.2s',
        ...(!notification.is_read && {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
          borderLeft: (theme) => `4px solid ${theme.palette.primary.main}`,
        }),
        '&:hover': {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
        },
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          mr: 2,
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          bgcolor: style.bgcolor,
          flexShrink: 0,
        }}
      >
        <Icon icon={style.icon} width={24} height={24} color={style.color} />
      </Box>

      {/* Content */}
      <ListItemText
        disableTypography
        primary={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 0.5,
                fontWeight: notification.is_read ? 400 : 600,
                flexGrow: 1,
              }}
            >
              {notification.title}
            </Typography>
            {!notification.is_read && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  flexShrink: 0,
                }}
              />
            )}
          </Stack>
        }
        secondary={
          <>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                mb: 1,
              }}
            >
              {notification.message}
            </Typography>

            {/* Timestamp and Action Button */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Icon icon="eva:clock-fill" width={14} height={14} />
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  {formatDistanceToNow(new Date(notification.created_at), {
                    addSuffix: true,
                  })}
                </Typography>
              </Box>

              {notification.action_url && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleActionClick}
                  endIcon={<Icon icon="eva:external-link-fill" width={14} height={14} />}
                  sx={{
                    minWidth: 'auto',
                    py: 0.25,
                    px: 1,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                  }}
                >
                  {getActionLabel(notification.action_type)}
                </Button>
              )}
            </Stack>
          </>
        }
      />
    </ListItem>
  );
}

// Helper function to get action button label
function getActionLabel(actionType) {
  const labels = {
    view_payment: 'View Payment',
    approve_request: 'Approve',
    view_details: 'View Details',
    check_visitor: 'Check Visitor',
    view_invoice: 'View Invoice',
    renew_subscription: 'Renew',
    default: 'View',
  };

  return labels[actionType] || labels.default;
}